import * as React from 'react';
import styled from 'styled-components';
import strings from '../../../strings/strings.json';
import { BodySmall, FlexCol, FlexColSm, FlexMdAlignCenter, Headline4 } from '../../../components/Elements';
import { Activity, ActivityType } from '../../../models/Activity';
import { breakpoints, sizes, theme } from '../../../theme';
import Button from '../../../components/buttons/Button';
import EditIcon from 'assets/icons/controls/EditIcon';
import ActivityTypeLabel from './ActivityTypeLabel';
import useWindowWidth from 'hooks/useWindowWidth';
import DurationLabel from 'components/DurationLabel';

type ActivityCardProps = {
    activity: Activity;
    isDragging: boolean;
    activitiesEditable: boolean;
    editActivityClick: () => void;
    unfocus: boolean;
};

export default function ActivityCard({
    activity,
    isDragging,
    activitiesEditable,
    editActivityClick,
    unfocus,
}: ActivityCardProps): JSX.Element {
    
    const { belowTabletWidth } = useWindowWidth();

    const generateSubtitle = () => {
        if (activity.type === ActivityType.QUESTIONNAIRE) {
            return (
                <FlexCol>
                    <BodySmall $nowrap>
                        {activity.questions.length} {strings.courseContentPage.questions}
                    </BodySmall>
                </FlexCol>
            );
        }

        if (activity.type === ActivityType.LIVE_TRAINING) {
            return (
                <FlexCol>
                    <DurationLabel durationInMins={activity.durationInMins ?? 0} hideTitle />
                </FlexCol>
            );
        }
    };

    let editButtonTitle = '';
    let editButtonAria = '';

    switch (activity.type) {
        case ActivityType.QUESTIONNAIRE:
            editButtonTitle = strings.courseContentPage.editQuestionnaireButton;
            editButtonAria = strings.courseContentPage.editQuestionnaireButtonAria;
            break;
        case ActivityType.LIVE_TRAINING:
            editButtonTitle = strings.courseContentPage.editLiveTrainingButton;
            editButtonAria = strings.courseContentPage.editLiveTrainingButtonAria;
            break;
        case ActivityType.PRESENTATION_SLIDE:
            editButtonTitle = strings.courseContentPage.editSlideButton;
            editButtonAria = strings.courseContentPage.editSlideButtonAria;
            break;
        case ActivityType.TASK:
        default:
            editButtonTitle = strings.courseContentPage.editTaskButton;
            editButtonAria = strings.courseContentPage.editTaskButtonAria;
            break;
    }

    return (
        <Grid $isDragging={isDragging}>
            <DetailsArea>
                <Headline4>{activity.title}</Headline4>
                <FlexMdAlignCenter>
                    <ActivityTypeLabel activityType={activity.type} buildMethod={activity.buildMethod} />
                    {generateSubtitle()}
                </FlexMdAlignCenter>
            </DetailsArea>

            {activitiesEditable && (
                <ButtonContainer>
                    <Button
                        title={editButtonTitle}
                        aria={editButtonAria}
                        onClick={() => editActivityClick()}
                        icon={<EditIcon />}
                        fullwidth={belowTabletWidth}
                        unfocus={unfocus}
                    />
                </ButtonContainer>
            )}
        </Grid>
    );
}

const Grid = styled.div<{ $isDragging: boolean }>`
    padding: ${sizes.spacingMd};
    border-radius: ${sizes.spacingSm};
    border: ${({ $isDragging }) => {
        return $isDragging ? `2px solid ${theme.primaryButtonBorderColour}` : `1px solid ${theme.cardBorder}`;
    }};
    box-shadow: 2px 4px 6px rgba(0, 0, 0, 0.1);
    background-color: ${({ $isDragging }) => {
        return $isDragging ? theme.buttonFocusBackgroundColour : 'none';
    }};

    display: grid;
    grid-template-areas: 'title button';
    grid-template-columns: 1fr;
    gap: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.md}) {
        grid-template-areas: 'title' 'subtitle' 'button';
        grid-template-columns: unset;
    }
`;

const DetailsArea = styled(FlexColSm)`
    grid-area: title;
`;

const ButtonContainer = styled.div`
    grid-area: button;
`;
